import { useMemo } from "react";
import { useSelector } from "react-redux";
import { OrderSummary } from "@app/domain";
import { RootState } from "@app/store";
import { useDocumentVisibility } from "@app/shared/hooks/useDocumentVisibility";
import { MapEtaOverlay } from "../MapView.types";
import { computeMarkerEta } from "../utils/computeMarkerEta";
import { findLastDropoffMarkerId, findPickupMarkerId } from "../utils/buildOrderMapMarkers";

/**
 * Public hook feature code calls: wires `computeMarkerEta`'s pure rule up to
 * the document-visibility ("userActive") signal and the order's own route,
 * and memoizes the result.
 */
export function useMarkerEta(order: OrderSummary, isDropoffTracking: boolean): MapEtaOverlay {
  const userActive = useDocumentVisibility();
  const language = useSelector((state: RootState) => state.setting.language);

  return useMemo(
    () =>
      computeMarkerEta({
        orderKind: order.kind,
        statusCode: order.status.code,
        etaAt: order.etaAt,
        userActive,
        isDropoffTracking,
        isLastDropOff: order.isLastDropOff,
        pickupMarkerId: findPickupMarkerId(order),
        lastDropoffMarkerId: findLastDropoffMarkerId(order),
        lastDropoffStatus: order.dropoffs[order.dropoffs.length - 1]?.status ?? null,
        language,
        timeFormat: order.timeFormat,
      }),
    [order, isDropoffTracking, userActive, language]
  );
}
